/**
 * Yelp REST Helpers (Story 23.5 AC #2, #3, #4)
 *
 * Per-endpoint typed wrappers around `safeFetchYelp`. Each helper:
 *   - Asserts a non-empty businessId (when scoped)
 *   - Passes the optional `expectedResultPath` for response-shape
 *     assertion (carries forward CallRail 22.1 + Semrush 20.4 + Moz 20.7 +
 *     Trustpilot 23.3 `expectedResultPath` invariant)
 *   - Threads the decrypted API Key into `safeFetchYelp`'s redaction context
 *
 * Single base URL constant: `YELP_API_BASE = "https://api.yelp.com/v3"`.
 * Yelp Fusion is read-only — no write-mutation host exists.
 */
import type { AdapterResult } from "../adapter";
import { adapterErr, adapterOk } from "../adapter";

import { buildYelpAdapterError, parseYelpApiError, sanitizeYelpErrorMessage } from "./errors";
import { safeFetchYelp } from "./safe-fetch";

/**
 * Re-map a generic `safeFetchYelp` failure result through the Yelp-specific
 * error parser. The generic safeFetch already maps HTTP status to an
 * AdapterErrorCode, but it doesn't know Yelp's 404 → account_not_found
 * mapping or its 429 daily-quota vs QPS-burst disambiguation. We re-parse
 * here using the providerMessage (which safeFetch captures as the response
 * body) so the Yelp-specific code/retryable values surface.
 */
function remapYelpFetchError(
  result: { success: false; error: { code: string; message: string; retryable: boolean; retryAfterMs?: number; providerStatus?: number; providerMessage?: string } },
  apiKey: string,
): AdapterResult<never> {
  const status = result.error.providerStatus;
  if (typeof status !== "number") {
    return result as unknown as AdapterResult<never>;
  }
  const parsed = parseYelpApiError(status, result.error.providerMessage);
  return adapterErr({
    code: parsed.code,
    message: sanitizeYelpErrorMessage(
      parsed.message ?? result.error.message,
      apiKey,
    ),
    retryable: parsed.retryable,
    retryAfterMs: parsed.retryAfterMs,
    providerStatus: status,
    providerMessage: result.error.providerMessage,
  });
}

/** Single base URL for Story 23.5 — Yelp Fusion has no separate write host. */
export const YELP_API_BASE = "https://api.yelp.com/v3";

/**
 * Yelp business ID / alias regex. Accepts opaque alphanumeric IDs (e.g.
 * `4kMBbn2-2VbnaqsKBeQbCQ`) AND URL slug aliases (e.g.
 * `greenlife-wellness-portland`). Both forms are valid path inputs to
 * `/v3/businesses/{id_or_alias}` per
 * docs.developer.yelp.com/reference/v3_business_info verified 2026-05-18.
 *
 * 1-128 char range: aliases run long when business name has many words.
 */
const BUSINESS_ID_REGEX = /^[a-zA-Z0-9_-]{1,128}$/;

/**
 * Validate a businessId synchronously. Throws a `TypeError` at the helper
 * level (NOT an adapter error) per AC #11 — these IDs come from
 * `integrations.account_id` and a missing/malformed value indicates a
 * code bug, not a transient API issue.
 *
 * Round 3 Red Team Attack #5 enumeration defense: the regex rejects any
 * shell-metacharacter / path-traversal / URL-injection payload.
 */
function assertBusinessId(businessId: string): void {
  if (!businessId || typeof businessId !== "string") {
    throw new TypeError(
      "Yelp helpers require a businessId — got empty or non-string value.",
    );
  }
  if (!BUSINESS_ID_REGEX.test(businessId)) {
    throw new TypeError(
      `Yelp businessId failed shape validation (expected alphanumeric + - _, 1-128 chars): "${businessId}"`,
    );
  }
}

// ============================================================================
// Business Details — `GET /v3/businesses/{businessId}` (Bearer auth, all tiers)
// ============================================================================

export interface YelpBusinessLocation {
  readonly address1?: string | null;
  readonly address2?: string | null;
  readonly address3?: string | null;
  readonly city?: string;
  readonly state?: string;
  readonly zip_code?: string;
  readonly country?: string;
  readonly display_address?: ReadonlyArray<string>;
}

export interface YelpBusinessCoordinates {
  readonly latitude: number;
  readonly longitude: number;
}

export interface YelpBusinessCategory {
  readonly alias: string;
  readonly title: string;
}

export interface YelpBusinessDetails {
  readonly id: string;
  readonly alias: string;
  readonly name: string;
  readonly image_url?: string | null;
  readonly is_closed: boolean;
  readonly is_claimed?: boolean;
  readonly url: string;
  readonly review_count: number;
  readonly categories: ReadonlyArray<YelpBusinessCategory>;
  readonly rating: number;
  readonly coordinates?: YelpBusinessCoordinates;
  readonly transactions?: ReadonlyArray<string>;
  readonly price?: string | null;
  readonly location?: YelpBusinessLocation;
  readonly phone?: string | null;
  readonly display_phone?: string | null;
  readonly photos?: ReadonlyArray<string>;
}

/**
 * Fetch the marquee business KPIs in a single API request.
 *
 * Response shape per docs.developer.yelp.com/reference/v3_business_info
 * verified 2026-05-18. ~1KB payload.
 *
 * Used by:
 *  - Connect-time API Key probe (validateYelpApiKey)
 *  - `fetchMetrics` Endpoint Group A
 *  - Cron health-check (validateConnection)
 *  - `discoverAccounts`
 */
export async function fetchYelpBusinessDetails(
  businessId: string,
  apiKey: string,
): Promise<AdapterResult<YelpBusinessDetails>> {
  assertBusinessId(businessId);

  const url = `${YELP_API_BASE}/businesses/${encodeURIComponent(businessId)}`;
  const result = await safeFetchYelp(
    url,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${apiKey}`,
        Accept: "application/json",
      },
    },
    { decryptedApiKey: apiKey },
  );

  if (!result.success) {
    return remapYelpFetchError(result, apiKey);
  }

  const response = result.data;
  if (!response.ok) {
    const body = await safeReadBody(response);
    return adapterErr(
      buildYelpAdapterError(response.status, body, { decryptedApiKey: apiKey }),
    );
  }

  let payload: unknown;
  try {
    payload = await response.json();
  } catch {
    return adapterErr({
      code: "invalid_response",
      message: "Yelp business-details response was not valid JSON.",
      retryable: false,
    });
  }

  if (!payload || typeof payload !== "object") {
    return adapterErr({
      code: "invalid_response",
      message: "Yelp business-details response had unexpected shape.",
      retryable: false,
    });
  }

  const obj = payload as Record<string, unknown>;
  // Shape assertion — required fields per AC #4 expectedResultPath invariant.
  if (typeof obj.id !== "string") {
    return adapterErr({
      code: "invalid_response",
      message:
        "Yelp business-details response missing required `id` field — provider shape drift.",
      retryable: false,
    });
  }
  // Round 1 review fix (Edge Case Hunter Finding 12 / Blind Hunter):
  // require numeric `rating` + `review_count` so downstream snapshot writes
  // never persist sentinel-zero rows on shape drift.
  if (typeof obj.rating !== "number" || !Number.isFinite(obj.rating as number)) {
    return adapterErr({
      code: "invalid_response",
      message:
        "Yelp business-details response missing/invalid `rating` field — provider shape drift.",
      retryable: false,
    });
  }
  if (
    typeof obj.review_count !== "number" ||
    !Number.isFinite(obj.review_count as number)
  ) {
    return adapterErr({
      code: "invalid_response",
      message:
        "Yelp business-details response missing/invalid `review_count` field — provider shape drift.",
      retryable: false,
    });
  }

  return adapterOk(payload as YelpBusinessDetails);
}

// ============================================================================
// Reviews — `GET /v3/businesses/{businessId}/reviews?limit=N` (Plus+ tier)
// ============================================================================

export interface YelpReviewUser {
  readonly id: string;
  readonly name: string;
  readonly profile_url?: string | null;
  readonly image_url?: string | null;
}

export interface YelpReview {
  readonly id: string;
  readonly rating: number;
  readonly user: YelpReviewUser;
  readonly text: string;
  readonly time_created: string;
  readonly url: string;
}

export interface YelpReviewsResponse {
  readonly reviews: ReadonlyArray<YelpReview>;
  readonly total: number;
  readonly possible_languages?: ReadonlyArray<string>;
}

/**
 * Fetch the 3 most recent review excerpts (Enhanced default — Premium can
 * request up to 7 but Story 23.5 clamps to 3 to conserve daily call budget).
 *
 * Response shape per docs.developer.yelp.com/reference/v3_business_reviews
 * verified 2026-05-18.
 *
 * **PAID-TIER GATED** — returns 403 on Starter tier. The caller is expected
 * to consult `metadata.reviewsEndpointEntitled` BEFORE calling this helper;
 * if it skips the gating check and hits 403, the gating-fallback in
 * `fetchMetrics` downgrades the flag for next sync.
 */
export async function fetchYelpReviews(
  businessId: string,
  apiKey: string,
  limit: number = 3,
): Promise<AdapterResult<YelpReviewsResponse>> {
  assertBusinessId(businessId);
  // Round 1 review fix (Edge Case Hunter Finding 1 + Blind Hunter): handle
  // NaN / Infinity / negative inputs defensively before the URL is built.
  const clampedLimit = Number.isFinite(limit)
    ? Math.min(Math.max(1, Math.floor(limit)), 3)
    : 3;

  const url = `${YELP_API_BASE}/businesses/${encodeURIComponent(
    businessId,
  )}/reviews?limit=${clampedLimit}`;
  const result = await safeFetchYelp(
    url,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${apiKey}`,
        Accept: "application/json",
      },
    },
    { decryptedApiKey: apiKey },
  );

  if (!result.success) {
    return remapYelpFetchError(result, apiKey);
  }

  const response = result.data;
  if (!response.ok) {
    const body = await safeReadBody(response);
    return adapterErr(
      buildYelpAdapterError(response.status, body, { decryptedApiKey: apiKey }),
    );
  }

  let payload: unknown;
  try {
    payload = await response.json();
  } catch {
    return adapterErr({
      code: "invalid_response",
      message: "Yelp reviews response was not valid JSON.",
      retryable: false,
    });
  }

  if (!payload || typeof payload !== "object") {
    return adapterErr({
      code: "invalid_response",
      message: "Yelp reviews response had unexpected shape.",
      retryable: false,
    });
  }

  const obj = payload as Record<string, unknown>;
  if (!Array.isArray(obj.reviews)) {
    return adapterErr({
      code: "invalid_response",
      message:
        "Yelp reviews response missing `reviews` array — provider shape drift.",
      retryable: false,
    });
  }

  return adapterOk({
    reviews: obj.reviews as ReadonlyArray<YelpReview>,
    total: typeof obj.total === "number" ? obj.total : 0,
    possible_languages: Array.isArray(obj.possible_languages)
      ? (obj.possible_languages as ReadonlyArray<string>)
      : undefined,
  });
}

// ============================================================================
// API Key validation — connect-time probe
// ============================================================================

/**
 * Lightweight credential validation. Used by the connect route AND the
 * cron's healthCheck handler. Cheapest possible call (1 request, ~1KB
 * response).
 */
export async function validateYelpApiKey(
  apiKey: string,
  businessIdForProbe: string,
): Promise<AdapterResult<YelpBusinessDetails>> {
  return fetchYelpBusinessDetails(businessIdForProbe, apiKey);
}

// ============================================================================
// Helpers
// ============================================================================

async function safeReadBody(response: Response): Promise<string | undefined> {
  try {
    return await response.text();
  } catch {
    return undefined;
  }
}
